import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, CheckCircle, AlertTriangle, Phone, ChevronRight, RefreshCw, ScanLine } from "lucide-react";
import axios from "axios";
import { KioskViewport, useKioskSpeech } from "../../components/ui/kiosk-viewport";
import { KioskHeader } from "../../components/ui/kiosk-header";

const API = '/api';
const IDLE_TIMEOUT = 60;
const BADGE_REVIEW_MESSAGE = "Your Blue Badge details have been read. Please check the details below before confirming.";

export default function KioskBadgeReview() {
  const navigate = useNavigate();
  const location = useLocation();
  const { registrationData, frontImage, rearImage, ocrData } = location.state || {};
  
  // Initialize form with OCR data, using holder_name as default full_name if blank
  const [formData, setFormData] = useState({
    holder_name: ocrData?.holder_name || registrationData?.full_name || '',
    badge_number: ocrData?.badge_number || '',
    expiry_date: ocrData?.expiry_date || '',
    issuing_authority: ocrData?.issuing_authority || '',
    issue_date: ocrData?.issue_date || ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const idleTimerRef = useRef(null);

  const confidence = ocrData?.confidence || 0;
  const isHighConfidence = confidence >= 0.5;
  
  // Log OCR data received
  useEffect(() => {
    console.log('[REVIEW] === OCR Data Received ===');
    console.log('[REVIEW] Holder Name:', ocrData?.holder_name || '(empty)');
    console.log('[REVIEW] Badge Number:', ocrData?.badge_number || '(empty)');
    console.log('[REVIEW] Expiry Date:', ocrData?.expiry_date || '(empty)');
    console.log('[REVIEW] Issuing Authority:', ocrData?.issuing_authority || '(empty)');
    console.log('[REVIEW] Confidence:', Math.round((ocrData?.confidence || 0) * 100) + '%');
  }, [ocrData]);
  
  // Check if we have images
  const hasFrontImage = !!frontImage;
  const hasRearImage = !!rearImage;
  const hasBothImages = hasFrontImage && hasRearImage;
  
  // Central speech controller - speak badge review guidance
  useKioskSpeech('KioskBadgeReview', BADGE_REVIEW_MESSAGE, !!registrationData);

  useEffect(() => {
    if (!registrationData) {
      navigate("/kiosk/category");
    }
  }, [registrationData, navigate]);

  useEffect(() => {
    const resetIdleTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => navigate('/kiosk/attract'), IDLE_TIMEOUT * 1000);
    };
    const events = ['mousedown', 'touchstart', 'keydown', 'input'];
    events.forEach(e => document.addEventListener(e, resetIdleTimer, { passive: true }));
    resetIdleTimer();
    return () => {
      events.forEach(e => document.removeEventListener(e, resetIdleTimer));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [navigate]);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      // Build comprehensive payload with all required fields
      const payload = {
        // Registration data from form
        vehicle_registration: registrationData?.vehicle_registration || '',
        full_name: formData.holder_name || registrationData?.full_name || '', // Use OCR holder_name if full_name blank
        email: registrationData?.email || '',
        phone: registrationData?.phone || '',
        category: registrationData?.category || 'disabled_blue_badge',
        
        // Badge details from OCR (editable)
        badge_number: formData.badge_number || '',
        badge_expiry: formData.expiry_date || '',
        badge_holder_name: formData.holder_name || '',
        badge_issuing_authority: formData.issuing_authority || '',
        
        // OCR metadata
        ocr_confidence: confidence,
        requires_review: !isHighConfidence,
        
        // Image capture status
        front_image_present: hasFrontImage,
        rear_image_present: hasRearImage,
        
        // Legacy badge_details for backward compatibility
        badge_details: {
          holder_name: formData.holder_name,
          badge_number: formData.badge_number,
          expiry_date: formData.expiry_date,
          issuing_authority: formData.issuing_authority,
          issue_date: formData.issue_date
        }
      };
      
      console.log('[SUBMIT] === Final Registration Payload ===');
      console.log('[SUBMIT] vehicle_registration:', payload.vehicle_registration);
      console.log('[SUBMIT] full_name:', payload.full_name);
      console.log('[SUBMIT] email:', payload.email);
      console.log('[SUBMIT] phone:', payload.phone);
      console.log('[SUBMIT] badge_number:', payload.badge_number);
      console.log('[SUBMIT] badge_expiry:', payload.badge_expiry);
      console.log('[SUBMIT] badge_holder_name:', payload.badge_holder_name);
      console.log('[SUBMIT] badge_issuing_authority:', payload.badge_issuing_authority);
      console.log('[SUBMIT] ocr_confidence:', payload.ocr_confidence);
      console.log('[SUBMIT] requires_review:', payload.requires_review);
      console.log('[SUBMIT] front_image_present:', payload.front_image_present);
      console.log('[SUBMIT] rear_image_present:', payload.rear_image_present);
      
      await axios.post(`${API}/registrations`, payload);
      navigate("/kiosk/confirmation", {
        state: {
          success: true,
          requiresReview: !isHighConfidence,
          registrationData: payload
        }
      });
    } catch (err) {
      console.error('[SUBMIT] Error:', err);
      setError("Failed to submit registration. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!registrationData) return null;

  return (
    <KioskViewport>
      <div 
        data-testid="kiosk-badge-review"
        style={{ 
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
          overflow: 'hidden',
          background: '#ffffff'
        }}
      >
        {/* STANDARDIZED HEADER */}
        <KioskHeader showDateTime={true} showProgress={true} currentStep={3} />

        {/* BACK BUTTON - TOP OF CONTENT */}
        <div style={{ padding: '16px 40px', background: 'white' }}>
          <button 
            onClick={() => navigate("/kiosk/badge-scan", { state: { registrationData } })}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '14px 24px',
              borderRadius: '14px',
              background: '#1e3a5f',
              border: 'none',
              cursor: 'pointer'
            }}
            data-testid="back-button"
          >
            <ArrowLeft size={28} color="white" />
            <span style={{ color: 'white', fontSize: '20px', fontWeight: '700' }}>Back</span>
          </button>
        </div>

        {/* HERO SECTION */}
        <div style={{ position: 'relative', height: '400px', overflow: 'hidden', flexShrink: 0 }}>
          <img src="/cityscape.png" alt="Background" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center bottom' }} />
          <img src="/tracy.png" alt="Tracy" style={{ position: 'absolute', right: '-20px', bottom: '-40px', height: '480px', zIndex: 10 }} />
          
          <div style={{
            position: 'absolute', top: '30px', left: '40px', width: '500px',
            background: '#fef9e7', borderRadius: '24px', padding: '24px 28px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)', zIndex: 20
          }}>
            <h3 style={{ fontSize: '28px', fontWeight: '700', color: '#1e3a5f', margin: '0 0 12px 0' }}>
              {isHighConfidence ? 'Badge Scanned Successfully' : 'Please Check Details'}
            </h3>
            <p style={{ fontSize: '18px', color: '#374151', lineHeight: 1.5, margin: 0 }}>
              {isHighConfidence 
                ? "Your badge details have been captured. Please review and confirm."
                : "We couldn't read all details clearly. Please check and correct any errors."}
            </p>
            <div style={{ position: 'absolute', right: '-22px', top: '50%', transform: 'translateY(-50%)', width: 0, height: 0, borderTop: '18px solid transparent', borderBottom: '18px solid transparent', borderLeft: '24px solid #fef9e7' }} />
          </div>
        </div>

        {/* FORM SECTION */}
        <div style={{
          flex: 1, padding: '24px 40px', background: 'linear-gradient(180deg, #f8fafc 0%, #e0f2fe 100%)',
          display: 'flex', flexDirection: 'column', gap: '16px', overflow: 'auto'
        }}>
          {/* Dual Image Display */}
          {(hasFrontImage || hasRearImage) && (
            <div style={{ marginBottom: '8px' }}>
              <h4 style={{ fontSize: '18px', fontWeight: '700', color: '#1e3a5f', margin: '0 0 16px 0' }}>
                Captured Badge Images
              </h4>
              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                {/* Front Image */}
                <div style={{ flex: '1', minWidth: '200px', maxWidth: '300px' }}>
                  <p style={{ fontSize: '14px', fontWeight: '600', color: '#64748b', marginBottom: '8px' }}>Front Side</p>
                  <div style={{
                    borderRadius: '12px', overflow: 'hidden',
                    border: hasFrontImage ? '3px solid #22c55e' : '3px solid #f59e0b',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    background: hasFrontImage ? '#fff' : '#fef3c7',
                    aspectRatio: '4/3',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    {hasFrontImage ? (
                      <img 
                        src={frontImage} 
                        alt="Front of Blue Badge" 
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        data-testid="front-badge-image"
                      />
                    ) : (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#92400e' }}>
                        <AlertTriangle size={32} color="#f59e0b" />
                        <p style={{ fontSize: '14px', marginTop: '8px' }}>Not captured</p>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Rear Image */}
                <div style={{ flex: '1', minWidth: '200px', maxWidth: '300px' }}>
                  <p style={{ fontSize: '14px', fontWeight: '600', color: '#64748b', marginBottom: '8px' }}>Rear Side</p>
                  <div style={{
                    borderRadius: '12px', overflow: 'hidden',
                    border: hasRearImage ? '3px solid #22c55e' : '3px solid #f59e0b',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    background: hasRearImage ? '#fff' : '#fef3c7',
                    aspectRatio: '4/3',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    {hasRearImage ? (
                      <img 
                        src={rearImage} 
                        alt="Rear of Blue Badge" 
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        data-testid="rear-badge-image"
                      />
                    ) : (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#92400e' }}>
                        <AlertTriangle size={32} color="#f59e0b" />
                        <p style={{ fontSize: '14px', marginTop: '8px' }}>Not captured</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Status indicator */}
              <div style={{
                marginTop: '12px', padding: '12px 16px', borderRadius: '8px',
                background: hasBothImages ? '#dcfce7' : '#fef3c7',
                border: `2px solid ${hasBothImages ? '#22c55e' : '#f59e0b'}`,
                display: 'flex', alignItems: 'center', gap: '10px'
              }}>
                {hasBothImages ? (
                  <>
                    <CheckCircle size={20} color="#22c55e" />
                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#166534' }}>Both sides captured successfully</span>
                  </>
                ) : (
                  <>
                    <AlertTriangle size={20} color="#f59e0b" />
                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#92400e' }}>
                      {hasFrontImage ? 'Rear side not captured' : hasRearImage ? 'Front side not captured' : 'Images not captured'}
                    </span>
                  </>
                )}
              </div>
            </div>
          )}

          {/* No images at all */}
          {!hasFrontImage && !hasRearImage && (
            <div style={{
              padding: '16px 24px', borderRadius: '12px', 
              background: '#fef3c7', border: '2px solid #f59e0b',
              display: 'flex', alignItems: 'center', gap: '12px'
            }}>
              <AlertTriangle size={28} color="#f59e0b" />
              <span style={{ fontSize: '18px', fontWeight: '600', color: '#92400e' }}>
                No images captured. Please enter badge details manually.
              </span>
            </div>
          )}

          {/* Confidence Indicator - only show if OCR was attempted */}
          {(hasFrontImage || hasRearImage) && (
            <div style={{
              display: 'flex', flexDirection: 'column', gap: '8px', padding: '16px 24px',
              borderRadius: '12px', background: isHighConfidence ? '#dcfce7' : '#e0f2fe',
              border: `2px solid ${isHighConfidence ? '#22c55e' : '#3b82f6'}`
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {isHighConfidence ? <CheckCircle size={24} color="#22c55e" /> : <ScanLine size={24} color="#3b82f6" />}
                <span style={{ fontSize: '18px', fontWeight: '600', color: isHighConfidence ? '#166534' : '#1e40af' }}>
                  {confidence > 0 ? `OCR Confidence: ${Math.round(confidence * 100)}%` : 'Badge details extracted'}
                </span>
              </div>
              <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>
                Please review and correct any fields that may have been misread.
              </p>
            </div>
          )}

          {/* Form Fields - 2 columns */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            {[
              { key: 'holder_name', label: 'Badge Holder Name', required: true },
              { key: 'badge_number', label: 'Badge Number' },
              { key: 'expiry_date', label: 'Expiry Date' },
              { key: 'issuing_authority', label: 'Issuing Authority' }
            ].map((field) => (
              <div key={field.key} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '16px', fontWeight: '600', color: '#1e3a5f' }}>
                  {field.label} {field.required && <span style={{ color: '#ef4444' }}>*</span>}
                </label>
                <input
                  type="text"
                  value={formData[field.key]}
                  onChange={(e) => setFormData(prev => ({ ...prev, [field.key]: e.target.value }))}
                  style={{
                    padding: '16px 20px', borderRadius: '12px', border: '2px solid #e2e8f0',
                    fontSize: '20px', fontWeight: '500', color: '#1e293b',
                    outline: 'none', transition: 'border-color 0.2s'
                  }}
                  data-testid={`input-${field.key}`}
                />
              </div>
            ))}
          </div>

          {/* Read-only fields */}
          <div style={{
            background: '#f1f5f9', borderRadius: '12px', padding: '16px 24px',
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px'
          }}>
            <div>
              <label style={{ fontSize: '14px', color: '#64748b' }}>Vehicle Registration</label>
              <p style={{ fontSize: '20px', fontWeight: '700', color: '#1e3a5f', margin: '4px 0 0 0' }}>
                {registrationData?.vehicle_registration || 'N/A'}
              </p>
            </div>
            <div>
              <label style={{ fontSize: '14px', color: '#64748b' }}>Email</label>
              <p style={{ fontSize: '20px', fontWeight: '500', color: '#1e3a5f', margin: '4px 0 0 0' }}>
                {registrationData?.email || 'N/A'}
              </p>
            </div>
          </div>

          {error && (
            <div style={{ background: '#fef2f2', border: '2px solid #ef4444', borderRadius: '12px', padding: '16px', color: '#dc2626', fontSize: '18px', fontWeight: '600' }}>
              {error}
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '20px', marginTop: 'auto' }}>
            <button
              onClick={() => navigate("/kiosk/badge-scan", { state: { registrationData } })}
              style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px',
                padding: '20px', borderRadius: '16px', border: '3px solid #64748b',
                background: 'white', cursor: 'pointer', fontSize: '22px', fontWeight: '700', color: '#64748b'
              }}
            >
              <RefreshCw size={28} /> Rescan Badge
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting || !formData.holder_name}
              style={{
                flex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px',
                padding: '20px', borderRadius: '16px', border: 'none',
                background: submitting || !formData.holder_name ? '#94a3b8' : '#22c55e',
                cursor: submitting || !formData.holder_name ? 'not-allowed' : 'pointer',
                fontSize: '24px', fontWeight: '700', color: 'white',
                boxShadow: '0 8px 32px rgba(34,197,94,0.3)'
              }}
              data-testid="confirm-button"
            >
              {submitting ? 'Submitting...' : isHighConfidence ? 'Confirm & Register' : 'Submit for Review'}
              <ChevronRight size={28} />
            </button>
          </div>

          {/* HELP INFO - In content area, no footer */}
          <div style={{
            marginTop: 'auto',
            padding: '20px 32px',
            background: '#1e3a5f',
            borderRadius: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px'
          }}>
            <Phone size={24} color="#fbbf24" />
            <span style={{ color: 'white', fontSize: '18px', fontWeight: '600' }}>Need help? Call </span>
            <span style={{ color: '#fbbf24', fontSize: '22px', fontWeight: '700' }}>01292 833107</span>
          </div>
        </div>
      </div>
    </KioskViewport>
  );
}
